import { ButtonSecondary, Colors, ConfirmDialog, ModalDialog, Text } from '@amelio/ui-library';
import { useState } from 'react';

import { COMPENSATION_CAPTION, COMPENSATION_HISTORY_LIST } from '../compensationStyles';
import { CompensationLoadFailedState } from './CompensationLoadFailedState';
import { useReportFailure } from '../hooks/useReportFailure';
import { describeChangeValue, summarizeChange } from '../utils/changeSummary';

import type { ChangeValue } from '../utils/changeSummary';
import type { TranslationKey } from '@/i18n-key';
import type { CompensationEnvelope, CompensationVersion } from '@/types/compensation';

import {
    useGetCompensationEnvelopeVersions,
    useRestoreCompensationEnvelopeVersion,
} from '@/api/compensation';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { showSuccessToast } from '@/plugins/Toast';
import { toCalendarLocale } from '@/utils/dateUtils';
import { getLocalizedText } from '@/utils/localization';

/** Origin of a version, as the leaf key it maps to under `compensation.history.origins`. */
const ORIGIN_KEYS: Record<CompensationVersion['origin'], TranslationKey> = {
    Save: 'compensation.history.origins.save',
    Restore: 'compensation.history.origins.restore',
    Close: 'compensation.history.origins.close',
    Reopen: 'compensation.history.origins.reopen',
};

interface HistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    envelope: CompensationEnvelope;
    /** Whether the reader may put an old version back (HR). */
    canRestore: boolean;
}

/**
 * Every save, close, reopen and restore this envelope has been through — who did it, when, and
 * what changed. The differences arrive as stable field keys (never as sentences), so the history
 * of a decision reads in the language of whoever opens it, not the one it was saved in.
 */
export const HistoryModal = ({ isOpen, onClose, envelope, canRestore }: HistoryModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const reportFailure = useReportFailure();

    const [versionToRestore, setVersionToRestore] = useState<CompensationVersion | null>(null);

    const {
        data: versions,
        isError,
        refetch,
    } = useGetCompensationEnvelopeVersions(isOpen ? envelope.id : undefined);
    const { mutateAsync: restoreVersion, isPending: isRestoring } =
        useRestoreCompensationEnvelopeVersion();

    const employeeNameById = new Map(
        envelope.computed.lines.map((line) => [line.employeeId, line.displayName]),
    );
    const levelLabelById = new Map(
        envelope.levels.map((level) => [level.id, getLocalizedText(level.label)]),
    );

    /** Newest first, whatever order the endpoint answers in. A change history is read for what
     * just happened, and the modal opens at the top: listed oldest first, the version the reader
     * came for is the one they have to scroll past everything else to reach. */
    const orderedVersions = [...(versions ?? [])].sort((a, b) => b.number - a.number);

    const formatDateTime = (value: string) =>
        new Date(value).toLocaleString(toCalendarLocale(language), {
            dateStyle: 'medium',
            timeStyle: 'short',
        });

    /** A version stores raw values; the history reads them next to the screen they came from, so
     * they are written the same way that screen writes them. */
    const readValue = (value: ChangeValue | null): string => {
        if (value === null) {
            return '';
        }

        if (value.kind === 'text') {
            return value.text;
        }

        return t(value.key);
    };

    const handleRestore = async () => {
        if (!versionToRestore) {
            return;
        }

        try {
            await restoreVersion({ id: envelope.id, versionId: versionToRestore.id });
            setVersionToRestore(null);
            onClose();
            showSuccessToast({ message: t('compensation.history.restoreSuccess') });
        } catch (error) {
            reportFailure(error, 'compensation.history.restoreFailed');
        }
    };

    const renderVersions = () => {
        if (isError && !versions) {
            return <CompensationLoadFailedState onRetry={() => void refetch()} />;
        }

        if (!versions) {
            return (
                <Skeleton className={'flex flex-col gap-3'}>
                    <Bone className={'h-20 w-full'} />
                    <Bone className={'h-20 w-full'} />
                </Skeleton>
            );
        }

        if (orderedVersions.length === 0) {
            return <EmptyState message={t('compensation.history.empty')} />;
        }

        return (
            <ul className={COMPENSATION_HISTORY_LIST}>
                {orderedVersions.map((version) => (
                    <li
                        key={version.id}
                        className={'border-base-100 rounded-[12px] border p-[16px]'}
                        data-testid={`compensation-history-version-${version.number}`}
                    >
                        <div className={'flex flex-wrap items-baseline justify-between gap-2'}>
                            <Text
                                weight={'semibold'}
                                color={Colors.NAVY}
                            >
                                {t('compensation.history.version', {
                                    number: version.number,
                                    origin: t(ORIGIN_KEYS[version.origin]),
                                })}
                            </Text>

                            <Text
                                color={Colors.BASE_500}
                                className={COMPENSATION_CAPTION}
                            >
                                {`${version.createdBy?.name || t('compensation.history.unknownAuthor')} · ${formatDateTime(version.createdAt)}`}
                            </Text>
                        </div>

                        <ul className={'mt-2 flex flex-col gap-1'}>
                            {version.changes.map((change, index) => {
                                const summary = summarizeChange({
                                    change,
                                    resolveEmployeeName: (employeeId) =>
                                        employeeNameById.get(employeeId),
                                    resolveLevelLabel: (levelId) => levelLabelById.get(levelId),
                                });

                                return (
                                    <li
                                        key={`${change.field}-${change.employeeId ?? change.levelId ?? index}`}
                                        className={COMPENSATION_CAPTION}
                                    >
                                        <Text
                                            weight={'semibold'}
                                            color={Colors.NAVY}
                                            as={'span'}
                                        >
                                            {t(summary.key, summary.values)}
                                        </Text>

                                        {/* The strike-through already says "old value";
                                            `BASE_300` on white measures 2,35:1, which
                                            made the figure the reader is comparing
                                            against the one they cannot read. */}
                                        <Text
                                            color={Colors.BASE_500}
                                            as={'span'}
                                            decoration={'line-through'}
                                            className={'mx-1'}
                                        >
                                            {readValue(
                                                describeChangeValue({
                                                    field: change.field,
                                                    value: change.before,
                                                    language,
                                                    isCompanyWide: envelope.isCompanyWide,
                                                }),
                                            )}
                                        </Text>

                                        <Text
                                            color={Colors.BASE_500}
                                            as={'span'}
                                        >
                                            {`→ ${readValue(
                                                describeChangeValue({
                                                    field: change.field,
                                                    value: change.after,
                                                    language,
                                                    isCompanyWide: envelope.isCompanyWide,
                                                }),
                                            )}`}
                                        </Text>
                                    </li>
                                );
                            })}
                        </ul>

                        {canRestore ? (
                            <div className={'mt-3 flex justify-end'}>
                                <ButtonSecondary
                                    type={'button'}
                                    size={'sm'}
                                    onClick={() => setVersionToRestore(version)}
                                    dataTestId={`compensation-restore-${version.number}`}
                                >
                                    {t('compensation.history.restore')}
                                </ButtonSecondary>
                            </div>
                        ) : null}
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <>
            <ModalDialog
                isOpen={isOpen}
                onClose={onClose}
                closeAriaLabel={t('common.close')}
                title={t('compensation.history.title')}
                size={'md'}
                dataTestId={'compensation-history-modal'}
            >
                {renderVersions()}
            </ModalDialog>

            <ConfirmDialog
                isOpen={versionToRestore !== null}
                title={t('compensation.history.restoreTitle')}
                message={t('compensation.history.restoreMessage', {
                    number: versionToRestore?.number ?? 0,
                })}
                confirmLabel={t('compensation.history.restore')}
                cancelLabel={t('compensation.history.cancel')}
                onConfirm={handleRestore}
                onCancel={() => setVersionToRestore(null)}
                isLoading={isRestoring}
                dataTestId={'compensation-restore-dialog'}
            />
        </>
    );
};
